import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineRefresh, HiOutlineCheckCircle, HiOutlineXCircle,
  HiOutlineGlobe, HiOutlineExternalLink, HiOutlineSearch,
  HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineDownload,
} from 'react-icons/hi';
import { getCloudRunServicesList, getCloudRunServiceCosts, getServiceTags, setServiceTag } from '../services/api';
import ServiceTagDropdown, { groupAndSort, getTagLabel, TagFilter, stickyTh, stickyTd, stickyGroupTd } from '../components/ServiceTagDropdown';
import './CloudScheduler.css';

const SERVICE_TYPE = 'cloud-run';

function formatCost(cost, hasError) {
  if (hasError) return <span className="cost-cell" title="Cost data unavailable">—</span>;
  if (cost === null || cost === undefined) return <span className="cost-cell cost-loading">...</span>;
  if (cost === 0) return <span className="cost-cell">₹0</span>;
  return <span className="cost-cell">₹{Math.round(cost).toLocaleString('en-IN')}</span>;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function statusBadge(status) {
  if (status === 'Ready') return <span className="status-badge success"><HiOutlineCheckCircle size={12} /> Ready</span>;
  if (status === 'Failed') return <span className="status-badge error"><HiOutlineXCircle size={12} /> Failed</span>;
  if (status === 'Deploying') return <span className="status-badge warning">Deploying</span>;
  return <span className="status-badge neutral">{status || 'Unknown'}</span>;
}

function escapeCsvField(val) {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function csvHyperlink(url, label) {
  if (!url) return escapeCsvField(label || '');
  const safeUrl = url.replace(/"/g, '""');
  const safeLabel = String(label || url).replace(/"/g, '""');
  return `"=HYPERLINK(""${safeUrl}"",""${safeLabel}"")"`;
}

function CloudRunServices({ gcpProjects, selectedProjectId }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [tagMap, setTagMap] = useState({});
  const [tagFilter, setTagFilter] = useState('all');
  const [costMap, setCostMap] = useState({});
  const [costError, setCostError] = useState(false);

  const activeProject = gcpProjects.find(p => p._id === selectedProjectId);

  const fetchServices = useCallback(async (signal) => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await getCloudRunServicesList(activeProject._id);
      if (!signal?.cancelled) setServices(res.data || []);
    } catch (err) {
      if (!signal?.cancelled) {
        toast.error('Failed to load Cloud Run services: ' + (err.response?.data?.error || err.message));
        setServices([]);
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [activeProject]);

  const fetchTags = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await getServiceTags(activeProject._id);
      const map = {};
      for (const [key, val] of Object.entries(res.data || {})) {
        if (key.startsWith(SERVICE_TYPE + ':')) {
          map[key.replace(SERVICE_TYPE + ':', '')] = val;
        }
      }
      setTagMap(map);
    } catch { /* tags are optional */ }
  }, [activeProject]);

  const fetchCosts = useCallback(async () => {
    if (!activeProject) return;
    setCostError(false);
    try {
      const res = await getCloudRunServiceCosts(activeProject._id);
      setCostMap(res.data?.costs || {});
    } catch (err) {
      console.error('Cost fetch failed:', err?.response?.status, err?.message);
      setCostError(true);
    }
  }, [activeProject]);

  useEffect(() => {
    setServices([]);
    setExpandedRows({});
    setTagMap({});
    setCostMap({});
    setCostError(false);
    if (!activeProject) return;
    const signal = { cancelled: false };
    fetchServices(signal);
    fetchTags();
    fetchCosts();
    return () => { signal.cancelled = true; };
  }, [activeProject, fetchServices, fetchTags, fetchCosts]);

  const handleTagChange = async (serviceName, tag) => {
    const prev = { ...tagMap };
    setTagMap(m => tag ? { ...m, [serviceName]: tag } : (() => { const n = { ...m }; delete n[serviceName]; return n; })());
    try {
      await setServiceTag(activeProject._id, SERVICE_TYPE, serviceName, tag);
    } catch {
      setTagMap(prev);
      toast.error('Failed to update tag');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-hint">⇅</span>;
    return <span className="sort-active">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortFn = useCallback((list) => {
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortField === 'name') return mul * (a.name || '').localeCompare(b.name || '');
      if (sortField === 'repo') return mul * (a.repoName || '').localeCompare(b.repoName || '');
      if (sortField === 'region') return mul * (a.region || '').localeCompare(b.region || '');
      if (sortField === 'status') return mul * (a.status || '').localeCompare(b.status || '');
      if (sortField === 'branch') return mul * (a.branch || '').localeCompare(b.branch || '');
      return 0;
    });
  }, [sortField, sortDir]);

  const grouped = useMemo(() => {
    let list = services;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.region.toLowerCase().includes(q) ||
        (s.status || '').toLowerCase().includes(q) ||
        (s.repoName || '').toLowerCase().includes(q)
      );
    }
    return groupAndSort(list, tagMap, 'name', sortFn, tagFilter);
  }, [services, search, tagMap, sortFn, tagFilter]);

  const flatFiltered = useMemo(() => grouped.filter(r => !r._groupHeader), [grouped]);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  const COL_COUNT = 12;

  const downloadCSV = () => {
    if (flatFiltered.length === 0) return;
    const headers = ['Tag', 'Service Name', 'Region', 'Status', 'Repository', 'Tag / Branch', 'Cost MTD (INR)', 'Latest Revision', 'Service URL', 'Scaling Min', 'Scaling Max', 'Last Deployed'];
    const rows = flatFiltered.map(s => [
      escapeCsvField(getTagLabel(tagMap[s.name]) || ''),
      csvHyperlink(s.consoleUrl, s.name),
      escapeCsvField(s.region),
      escapeCsvField(s.status),
      csvHyperlink(s.repoUrl, s.repoName),
      escapeCsvField(s.branch || ''),
      escapeCsvField(costMap[s.name]?.totalCostINR != null ? Math.round(costMap[s.name].totalCostINR) : ''),
      escapeCsvField(s.latestRevision),
      s.uri ? csvHyperlink(s.uri, s.uri.replace('https://', '').substring(0, 50)) : escapeCsvField(''),
      escapeCsvField(s.scaling?.minInstances ?? 0),
      escapeCsvField(s.scaling?.maxInstances ?? 'auto'),
      escapeCsvField(s.updateTime || ''),
    ]);
    const csvContent = [
      headers.map(escapeCsvField).join(','),
      ...rows.map(r => r.join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cloud-run-services-${activeProject?.projectId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeProject) {
    return (
      <div className="cloud-scheduler page-container">
        <div className="gcp-page-header">
          <div className="gcp-page-title-row"><h1><HiOutlineGlobe size={28} /> Cloud Run Services</h1></div>
        </div>
        <div className="empty-state"><p>Select a GCP project to view Cloud Run Services.</p></div>
      </div>
    );
  }

  return (
    <div className="cloud-scheduler page-container">
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineGlobe size={28} /> Cloud Run Services</h1>
          <span className="pv-subtitle">{activeProject.projectName} — {flatFiltered.length} service{flatFiltered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="gcp-page-toolbar">
          <div className="header-search">
            <HiOutlineSearch size={14} />
            <input type="text" placeholder="Search services..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <TagFilter value={tagFilter} onChange={setTagFilter} />
          <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={loading || flatFiltered.length === 0} title="Download CSV">
            <HiOutlineDownload size={14} /> CSV
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchServices({})} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && services.length === 0 && (
        <div className="empty-state"><p>No Cloud Run services found in <strong>{activeProject.projectName}</strong>.</p></div>
      )}

      {grouped.length > 0 && (
        <div className="resource-table-wrap">
          <div className="resource-table-scroll">
          <table className="resource-table">
            <thead>
              <tr>
                <th style={stickyTh[0]}></th>
                <th style={stickyTh[1]}>Tag</th>
                <th className="sortable-th" style={stickyTh[2]} onClick={() => handleSort('name')}>Service Name <SortIcon field="name" /></th>
                <th className="sortable-th" onClick={() => handleSort('region')}>Region <SortIcon field="region" /></th>
                <th className="sortable-th" onClick={() => handleSort('status')}>Status <SortIcon field="status" /></th>
                <th className="sortable-th" onClick={() => handleSort('repo')}>Repository <SortIcon field="repo" /></th>
                <th className="sortable-th" onClick={() => handleSort('branch')}>Tag / Branch <SortIcon field="branch" /></th>
                <th>Cost (MTD)</th>
                <th>Latest Revision</th>
                <th>URL</th>
                <th>Scaling</th>
                <th>Last Deployed</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((row, i) => {
                if (row._groupHeader) {
                  return (
                    <tr key={`gh-${row.group}`} className="group-header-row">
                      <td style={stickyGroupTd[0]}></td>
                      <td style={stickyGroupTd[1]}></td>
                      <td style={stickyGroupTd[2]}>
                        <span className="group-label">
                          <span className={`group-dot ${row.group}`}></span>
                          {row.label}
                          <span className="group-count">({row.count})</span>
                        </span>
                      </td>
                      <td colSpan={COL_COUNT - 3}></td>
                    </tr>
                  );
                }
                const s = row;
                const key = s.fullName || i;
                const isOpen = expandedRows[key];
                const cost = costMap[s.name]?.totalCostINR ?? null;
                return (
                  <React.Fragment key={key}>
                    <tr className="expandable-row" onClick={() => toggleRow(key)}>
                      <td className="expand-toggle" style={stickyTd[0]}>
                        {isOpen ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
                      </td>
                      <td style={stickyTd[1]}>
                        <ServiceTagDropdown value={tagMap[s.name] || ''} onChange={(tag) => handleTagChange(s.name, tag)} />
                      </td>
                      <td className="cell-name" style={stickyTd[2]}>
                        {s.consoleUrl ? (
                          <a href={s.consoleUrl} target="_blank" rel="noopener noreferrer" className="trigger-link" onClick={e => e.stopPropagation()}>
                            {s.name} <HiOutlineExternalLink size={11} />
                          </a>
                        ) : s.name}
                      </td>
                      <td><span className="region-badge">{s.region}</span></td>
                      <td>{statusBadge(s.status)}</td>
                      <td>
                        {s.repoUrl ? (
                          <a href={s.repoUrl} target="_blank" rel="noopener noreferrer" className="repo-link" onClick={e => e.stopPropagation()}>
                            <code>{s.repoName}</code> <HiOutlineExternalLink size={11} />
                          </a>
                        ) : s.repoName ? <code>{s.repoName}</code> : '—'}
                      </td>
                      <td>{s.branch ? <code>{s.branch}</code> : '—'}</td>
                      <td>{formatCost(cost, costError)}</td>
                      <td><code>{s.latestRevision}</code></td>
                      <td>
                        {s.uri ? (
                          <a href={s.uri} target="_blank" rel="noopener noreferrer" className="cell-link" title={s.uri} onClick={e => e.stopPropagation()}>
                            {s.uri.replace('https://', '').substring(0, 35)}{s.uri.length > 43 ? '...' : ''}
                            <HiOutlineExternalLink size={11} />
                          </a>
                        ) : '—'}
                      </td>
                      <td>{s.scaling?.minInstances ?? 0} – {s.scaling?.maxInstances ?? 'auto'}</td>
                      <td>{formatTimestamp(s.updateTime)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="expand-detail-row">
                        <td colSpan={COL_COUNT}>
                          <div className="expand-detail">
                            <div className="detail-grid">
                              <div className="detail-item full-width">
                                <span className="detail-label">Full Name</span>
                                <span className="detail-value mono">{s.fullName || '—'}</span>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Ingress</span>
                                  <span className="detail-value">{String(s.ingress || '—').replace('INGRESS_TRAFFIC_', '')}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Launch Stage</span>
                                  <span className="detail-value">{s.launchStage || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Min Instances</span>
                                  <span className="detail-value">{s.scaling?.minInstances ?? 0}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Max Instances</span>
                                  <span className="detail-value">{s.scaling?.maxInstances ?? 'auto'}</span>
                                </div>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">CPU</span>
                                  <span className="detail-value">{s.container?.cpu || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Memory</span>
                                  <span className="detail-value">{s.container?.memory || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Created</span>
                                  <span className="detail-value">{formatTimestamp(s.createTime)}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Last Deployed</span>
                                  <span className="detail-value">{formatTimestamp(s.updateTime)}</span>
                                </div>
                              </div>
                              {s.repoName && (
                                <div className="detail-row">
                                  <div className="detail-item">
                                    <span className="detail-label">Repository</span>
                                    <span className="detail-value mono">
                                      {s.repoUrl ? (
                                        <a href={s.repoUrl} target="_blank" rel="noopener noreferrer" className="repo-link">
                                          {s.repoName} <HiOutlineExternalLink size={11} />
                                        </a>
                                      ) : s.repoName}
                                    </span>
                                  </div>
                                  {s.branch && (
                                    <div className="detail-item">
                                      <span className="detail-label">Branch</span>
                                      <span className="detail-value mono">{s.branch}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="detail-item full-width">
                                <span className="detail-label">Container Image</span>
                                <span className="detail-value mono">{s.container?.image || '—'}</span>
                              </div>
                              <div className="detail-row">
                                {s.uri && (
                                  <div className="detail-item">
                                    <span className="detail-label">Service URL</span>
                                    <a href={s.uri} target="_blank" rel="noopener noreferrer" className="detail-value cell-link">{s.uri} <HiOutlineExternalLink size={11} /></a>
                                  </div>
                                )}
                                {s.consoleUrl && (
                                  <div className="detail-item">
                                    <span className="detail-label">GCP Console</span>
                                    <a href={s.consoleUrl} target="_blank" rel="noopener noreferrer" className="detail-value trigger-link">
                                      Open in Console <HiOutlineExternalLink size={11} />
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!loading && services.length > 0 && flatFiltered.length === 0 && (
        <div className="empty-state"><p>No services match "<strong>{search}</strong>"</p></div>
      )}
    </div>
  );
}

export default CloudRunServices;
